import { defaultAllSelection } from "services/constants/selections";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import {
  checkEditorPermission,
  checkViewerPermission,
} from "services/utils/HelperFunction";
import { timeLineLabelList } from "services/constants";
import {
  themes,
  tableRowSx,
  tableRowSxTimeline,
  scrollTop,
  optionalAvathar,
  adminRoleName,
  supportLabel,
} from "services/constants";
import * as Icons from "@mui/icons-material";
import { Loader } from "components/Loader";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { useFetch } from "services/hooks/useFetch";
import { getContrastingTextColor } from "services/utils/colorChecker";
import { EditCheckInOut } from "../AttendanceStatus/Status/EditCheckInOut";
import { tooltipStyleProps } from "services/constants/Styles";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import { setFilterApi } from "services/Redux/userManagament";
import {
  ApprovalDateRangeFilter,
  filterBarPickerSx,
} from "components/CustomComponents/ApprovalDateRangeFilter";
import { FilterSection } from "pages/FilterContext/FilterRow";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useSaveView } from "components/CustomComponents/useSaveView";

const keys = ["user", "department"];

export const AttendanceTimeLine = ({
  leavePage,
  setLeavePage,
  colorCode,
  departmentDetails,
}) => {
  const {
    userDetails: currentUserDetails,
    userScreenAccess,
    domain,
    topbarCompanyId,
    topbarCompanyName,
    userDetails,
  } = useSelector((state) => state.tokenReducer);

  const dispatch = useDispatch();

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Attendance",
  );
  const subModuleId = teamZoneReports?.submodules?.find(
    (sub) => sub.sub_module_name === "Timeline View",
  )?.sub_module_id;

  // canEditCell below is a real edit action (editing a day's attendance),
  // correctly gated on editor permission. The useSaveView screenAccess is a
  // separate, read-level concern (filter/save-view persistence), so it gets
  // its own viewer-based check rather than sharing check_access -- same
  // split already applied to Payroll's Penalties screen and TeamZone's
  // LeaveSummary.
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Attendance",
    "Timeline View",
  );
  const viewer_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Attendance",
    "Timeline View",
  );

  // Only Name/Employee ID/Department are stable columns here — the date
  // columns are regenerated per selected date range and are always shown,
  // so they're intentionally excluded from the saved header view.
  const timelineStableHeaders = useMemo(
    () => [
      {
        key: "user",
        name: "Employee Name",
        type: "string",
        icon: <Icons.PersonOutline />,
      },
      {
        key: "employee_id",
        name: "Employee ID",
        type: "string",
        icon: <Icons.Badge fontSize="small" />,
      },
      {
        key: "department",
        name: "Department",
        type: "autocomplete",
        icon: <Icons.BusinessCenter />,
      },
    ],
    [],
  );

  const handleSavedViewChange = useCallback(() => {
    setLeavePage(0);
  }, [setLeavePage]);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "attendance_timeline_saved_views",
    fullHeaders: timelineStableHeaders,
    screenAccess: viewer_access,
    onViewChange: handleSavedViewChange,
  });

  const stickyColumnWidths = useMemo(
    () => ({
      user: 180,
      employee_id: 120,
    }),
    [],
  );

  const stickyColumnLeftOffsets = useMemo(() => {
    let runningLeft = 0;
    const offsets = {};

    visibleHeaders.forEach((header) => {
      if (header?.key === "user" || header?.key === "employee_id") {
        offsets[header.key] = runningLeft;
        runningLeft += stickyColumnWidths[header.key] || 0;
      }
    });

    return offsets;
  }, [stickyColumnWidths, visibleHeaders]);

  const getStickyColumnSx = useCallback(
    (key, isHeader = false) => {
      if (key !== "user" && key !== "employee_id") {
        return {};
      }

      return {
        position: { xs: "static", sm: "sticky" },
        left: { sm: stickyColumnLeftOffsets[key] ?? 0 },
        zIndex: { xs: "auto", sm: isHeader ? 12 : 4 },
        backgroundColor: isHeader ? themes.primary : "#fff",
        backgroundClip: "padding-box",
        boxShadow:
          key === "employee_id"
            ? "1px 0 0 0 rgba(224, 224, 224, 1)"
            : "none",
      };
    },
    [stickyColumnLeftOffsets],
  );

  // Rendered per visibleHeaders' order (not a fixed sequence) so dragging a
  // column to a new position in the Save View picker actually reorders it
  // on screen instead of just reordering the export/filter header list.
  const stableHeadCellByKey = useMemo(
    () => ({
      user: (
        <Mui.TableCell
          key="user"
          className="timeline-name-cell"
          sx={{
            color: themes.headLine,
            fontWeight: "bold",
            ...getStickyColumnSx("user", true),
          }}
        >
          Name
        </Mui.TableCell>
      ),
      employee_id: (
        <Mui.TableCell
          key="employee_id"
          className="timeline-employee-id-cell"
          sx={{
            color: themes.headLine,
            fontWeight: "bold",
            ...getStickyColumnSx("employee_id", true),
          }}
        >
          Employee ID
        </Mui.TableCell>
      ),
      department: (
        <Mui.TableCell
          key="department"
          className="timeline-department-cell"
          sx={{ color: themes.headLine, fontWeight: "bold" }}
        >
          Department
        </Mui.TableCell>
      ),
    }),
    [getStickyColumnSx],
  );

  const renderStableBodyCell = useCallback((key, attendancedata) => {
    switch (key) {
      case "user":
        return (
          <Mui.TableCell
            key="user"
            className="column-cell timeline-name-cell"
            sx={getStickyColumnSx("user")}
          >
            <Mui.Tooltip
              arrow
              slotProps={tooltipStyleProps}
              title={attendancedata?.user}
            >
              <Mui.Stack direction="row" alignItems="center">
                <Mui.Avatar
                  sx={{ width: 23, height: 23 }}
                  alt={attendancedata?.user?.charAt(0).toUpperCase()}
                  src={attendancedata?.image_url ?? optionalAvathar}
                />
                <Mui.Typography
                  noWrap
                  sx={{ cursor: "pointer", paddingLeft: 1 }}
                >
                  {attendancedata?.user?.length > 15
                    ? attendancedata?.user.slice(0, 15) + "..."
                    : attendancedata?.user}
                </Mui.Typography>
              </Mui.Stack>
            </Mui.Tooltip>
          </Mui.TableCell>
        );
      case "employee_id":
        return (
          <Mui.TableCell
            key="employee_id"
            className="column-cell timeline-employee-id-cell"
            sx={getStickyColumnSx("employee_id")}
          >
            <Mui.Typography noWrap>
              {attendancedata?.employee_id ?? "N/A"}
            </Mui.Typography>
          </Mui.TableCell>
        );
      case "department":
        return (
          <Mui.TableCell
            key="department"
            className="column-cell timeline-department-cell"
          >
            <Mui.Tooltip
              arrow
              slotProps={tooltipStyleProps}
              title={attendancedata?.department}
            >
              <Mui.Typography
                noWrap
                sx={{ cursor: "pointer", paddingLeft: 0.3 }}
              >
                {attendancedata?.department?.length > 15
                  ? attendancedata?.department.slice(0, 15) + "..."
                  : attendancedata?.department}
              </Mui.Typography>
            </Mui.Tooltip>
          </Mui.TableCell>
        );
      default:
        return null;
    }
  }, [getStickyColumnSx]);

  const [trigger, setTrigger] = useState(true);
  const { globalSearchValue } = useSelector((state) => state.tokenReducer);
  const [selectedLabel, setSelectedLabel] = useState(["All"]);
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [departId, setDepartId] = useState(defaultAllSelection);
  const [leaverowsPerPage, setLeaveRowsPerPage] = useState(25);
  const [shiftDetails, setShiftDetails] = useState({});
  const [openSidePeek, setOpenSidePeek] = useState(false);
  const [editState, setEditState] = useState(false);
  const containerRef = useRef(null);
  const [departmentName, setDepartmentName] = useState("All Department");
  const [attendanceDataDetails, setAttendanceDataDetails] = useState([]);
  const [filteredDataForCustomFilter, setFilteredDataForCustomFilter] =
    useState([]);
  const [isFilterInitialized, setIsFilterInitialized] = useState(false);
  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    moment().subtract(1, "days").format("YYYY-MM-DD"),
  );
  const [dateRangeLabel, setDateRangeLabel] = useState("Last 7 Days");

  const {
    result: attendanceDetails,
    setLoad: reCallPayScheduleApi,
    loading,
  } = useFetch(
    `${domain}attendance-timeline/?department_id=${departId}&user_id=${
      currentUserDetails?.id
    }&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}&status=${
      selectedLabel.includes("All") || selectedLabel?.length === 0
        ? ""
        : `[${selectedLabel}]`
    }`,
  );

  const statusLabelMap = useMemo(() => {
    if (!Array.isArray(colorCode)) return {};
    return colorCode.reduce((acc, item) => {
      acc[item.status] = item.label;
      return acc;
    }, {});
  }, [colorCode]);

  const handleChangeLeavePage = (event, newPage) => {
    setLeavePage(newPage);
  };

  const handleChangeLeaveRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };
  useEffect(() => {
    if (attendanceDetails?.attendance_data) {
      dispatch(setFilterApi(true));
    }
  }, [
    attendanceDetails?.attendance_data,
    attendanceDetails?.attendance_data?.length,
    dispatch,
  ]);

  useEffect(() => {
    const recallApi = () => {
      setEditState(false);
      reCallPayScheduleApi(true);
      dispatch(setFilterApi(true));
    };
    if (editState && !openSidePeek) {
      recallApi();
    }
  }, [dispatch, editState, openSidePeek, reCallPayScheduleApi]);

  useEffect(() => {
    reCallPayScheduleApi(true);
  }, [topbarCompanyId, reCallPayScheduleApi]);

  useEffect(() => {
    if (
      attendanceDetails?.attendance_data?.length !== 0 ||
      attendanceDetails?.attendance_data?.length === 0
    ) {
      const query = globalSearchValue.toString().toLowerCase();
      const filteredResults = attendanceDetails?.attendance_data?.filter(
        (item) => {
          const baseMatch = keys.some((key) =>
            item?.[key]?.toString().toLowerCase().includes(query),
          );
          if (baseMatch) return true;
          return item?.attendance?.some((day) =>
            [day?.legend, day?.date, day?.shift_name, day?.status]
              .filter(Boolean)
              .some((value) => value.toString().toLowerCase().includes(query)),
          );
        },
      );
      setAttendanceDataDetails(filteredResults);
      setLeavePage(0);
    }
  }, [globalSearchValue, attendanceDetails, setLeavePage]);

  const handleStartDate = useCallback(
    async (selectedStartDate, selectedEndDate, rangeLabel) => {
      setStartDate(moment(selectedStartDate).format("YYYY-MM-DD"));
      setEndDate(moment(selectedEndDate).format("YYYY-MM-DD"));
      setDateRangeLabel(rangeLabel || "Custom Range");
      if (!trigger) {
        setTimeout(function () {
          reCallPayScheduleApi(true);
        }, 150);
      } else {
        setTrigger(false);
      }
    },
    [trigger, reCallPayScheduleApi],
  );

  const handleDepartmentName = useCallback(
    (event, data) => {
      if (data) {
        setDepartmentId(data?.id);
        setDepartId(data?.id);
        setDepartmentName(data?.label);
        reCallPayScheduleApi(true);
      } else {
        setDepartId("");
        setDepartmentId("");
        setDepartmentName("");
      }
    },
    [reCallPayScheduleApi],
  );

  useEffect(() => {
    scrollTop(containerRef);
  }, [leavePage]);

  const transformDataForFilter = useCallback((data) => {
    if (!data?.attendance_data) return [];
    return data.attendance_data.map((employee) => {
      const attendanceStatuses = employee.attendance.map((day) => day.legend);
      const attendanceDayFields = employee.attendance.reduce((acc, day) => {
        acc[`date_${day.date}`] = day.legend;
        return acc;
      }, {});
      return {
        ...attendanceDayFields,
        user_id: employee.user_id,
        user: employee.user,
        department: employee.department,
        department_id: employee.department_id,
        image_url: employee.image_url,
        latest_pay_date: employee.latest_pay_date,
        employee_id: employee.employee_id,
        user_type: employee.user_type,
        attendance_statuses: attendanceStatuses.join(","),
        has_absent: attendanceStatuses.includes("A"),
        has_present: attendanceStatuses.includes("P"),
        has_late: attendanceStatuses.includes("L1"),
        has_weekoff: attendanceStatuses.includes("W"),
        has_holiday: attendanceStatuses.includes("H"),
        has_leave: attendanceStatuses.includes("L"),
      };
    });
  }, []);

  const timelineDateHeaders = useMemo(
    () =>
      attendanceDetails?.headers?.length
        ? attendanceDetails.headers
        : attendanceDataDetails?.[0]?.attendance?.map((day) => ({
            date: day.date,
            day_number: moment(day.date).format("DD"),
            day: moment(day.date).format("ddd"),
          })) || [],
    [attendanceDataDetails, attendanceDetails?.headers],
  );

  const timeLineFilterHeaders = useMemo(() => {
    const dynamicDateHeaders =
      timelineDateHeaders.map((header) => ({
        key: `date_${header.date}`,
        name: `${header.day_number} ${header.day}`,
        type: "autocomplete",
        icon: <Icons.DateRange />,
      })) || [];

    return [...visibleHeaders, ...dynamicDateHeaders];
  }, [timelineDateHeaders, visibleHeaders]);

  const customFilterData = useMemo(() => {
    return transformDataForFilter(attendanceDetails);
  }, [attendanceDetails, transformDataForFilter]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentName || "All",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [departmentName, endDate, startDate, topbarCompanyName],
  );

  const handleFilteredDataChangeForCustomFilter = useCallback(
    (filteredData) => {
      if (!filteredData || filteredData.length === 0) {
        setAttendanceDataDetails([]);
        setFilteredDataForCustomFilter([]);
        return;
      }
      const reconstructedData = filteredData
        .map((flatEmployee) => {
          return attendanceDetails?.attendance_data?.find(
            (emp) => emp.user_id === flatEmployee.user_id,
          );
        })
        .filter(Boolean);

      setAttendanceDataDetails(reconstructedData);
      setFilteredDataForCustomFilter(filteredData);

      if (leavePage * leaverowsPerPage >= reconstructedData.length) {
        setLeavePage(0);
      }
    },
    [leavePage, leaverowsPerPage, setLeavePage, attendanceDetails],
  );

  useEffect(() => {
    if (attendanceDetails?.attendance_data && !isFilterInitialized) {
      const transformed = transformDataForFilter(attendanceDetails);
      setFilteredDataForCustomFilter(transformed);
      setAttendanceDataDetails(attendanceDetails.attendance_data);
      setIsFilterInitialized(true);
    }
  }, [attendanceDetails, transformDataForFilter, isFilterInitialized]);

  useEffect(() => {
    if (attendanceDetails?.attendance_data) {
      setIsFilterInitialized(false);
    }
  }, [
    attendanceDetails?.attendance_data,
    attendanceDetails?.attendance_data?.length,
  ]);

  return (
    <React.Fragment>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <FilterSection
          filterLabel="Filters"
          filters={[
            {
              width: 300,
              props: {
                dataList:
                  departmentDetails?.map((obj) => ({
                    ...obj,
                    label: obj.department_name,
                  })) || [],
                selectedValue: departmentName,
                updateDataFunction: handleDepartmentName,
                Icons: PersonOutlineIcon,
                staticLabel: "Department",
              },
            },
            {
              width: 300,
              props: {
                dataList: timeLineLabelList.map((obj) => ({
                  ...obj,
                  label: obj.label,
                  id: obj.label,
                })),
                selectedValue: selectedLabel[0] || "All",
                updateDataFunction: (event, data) => {
                  if (data) {
                    const newLabels = Array.isArray(data)
                      ? data.map((d) => d.label)
                      : [data.label];
                    setSelectedLabel(
                      newLabels.length > 1
                        ? newLabels.filter((label) => label !== "All")
                        : newLabels,
                    );
                    setTimeout(() => {
                      reCallPayScheduleApi(true);
                    }, 150);
                  }
                },
                Icons: PersonOutlineIcon,
                staticLabel: "Labels",
              },
            },
            {
              width: 350,
              component: (
                <ApprovalDateRangeFilter
                  startDate={startDate}
                  endDate={endDate}
                  dateRangeLabel={dateRangeLabel}
                  onChange={handleStartDate}
                  showToday={true}
                  includeCurrentDate={false}
                  allowFutureDate={false}
                  sx={filterBarPickerSx}
                />
              ),
            },
          ]}
        />
        <Mui.Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pt: 1, minWidth: 0 }}>
          <CustomFilter
            key="timeline-custom-filter"
            title={"Timeline Record"}
            titleOverride={titleOverride}
            filterHeaders={timeLineFilterHeaders}
            data={customFilterData}
            filteredData={filteredDataForCustomFilter}
            onFilteredDataChange={handleFilteredDataChangeForCustomFilter}
            screenAccess={true}
            showtitle_count={true}
            showFilter={false}
            filters={null}
            hideCount={true}
            newButtonComponent={newButtonComponent}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
          />
        </Mui.Box>
      </FilterProvider>
      {loading ? (
        <Loader info="Loading..." />
      ) : (
        <Mui.Grid container sx={{ minWidth: 0, overflow: "hidden" }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              maxHeight: 660,
              mx: { xs: 0, sm: 2 },
              overflowX: "auto",
            }}
          >
            <Mui.Table
              className="border"
              sx={{
                minWidth: 720,
                tableLayout: "auto",
                borderCollapse: "separate",
                borderSpacing: 0,
                "& .timeline-name-cell": {
                  minWidth: { xs: 120, sm: 180 },
                  maxWidth: { xs: 120, sm: "none" },
                },
                "& .timeline-employee-id-cell": {
                  minWidth: { xs: 96, sm: 120 },
                  maxWidth: { xs: 96, sm: "none" },
                  whiteSpace: "nowrap",
                },
                "& .timeline-department-cell": {
                  minWidth: { xs: 130, sm: 160 },
                },
              }}
            >
              <Mui.TableHead
                sx={{
                  backgroundColor: themes.primary,
                  position: "sticky",
                  top: -1,
                  zIndex: 10,
                }}
              >
                <Mui.TableRow sx={tableRowSx}>
                  {visibleHeaders.map(
                    (header) => stableHeadCellByKey[header.key],
                  )}
                  {timelineDateHeaders.map((header, index) => (
                    <Mui.TableCell
                      key={index}
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        padding: "4px 2px",
                        minWidth: 38,
                        width: 38,
                      }}
                    >
                      <Mui.Stack>
                        <Mui.Typography
                          textAlign="center"
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                            fontSize: 10,
                          }}
                        >
                          {header.day_number}
                        </Mui.Typography>
                        <Mui.Typography
                          textAlign="center"
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                            fontSize: 10,
                          }}
                        >
                          {header.day}
                        </Mui.Typography>
                      </Mui.Stack>
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {attendanceDataDetails?.length === 0 && !loading ? (
                  <Mui.TableRow sx={{ height: "750px" }}>
                    <Mui.TableCell
                      colSpan={
                        timelineDateHeaders.length +
                        visibleHeaders.length
                      }
                      sx={{
                        padding: 2,
                        position: "absolute",
                        width: "97%",
                      }}
                    >
                      <Mui.Alert severity="info">
                        {attendanceDetails?.attendance_data?.length === 0
                          ? "No data available."
                          : "No matching data found for your search."}
                      </Mui.Alert>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : (
                  attendanceDataDetails
                    ?.slice(
                      leavePage * leaverowsPerPage,
                      leavePage * leaverowsPerPage + leaverowsPerPage,
                    )
                    .map((attendancedata) => (
                      <Mui.TableRow
                        key={attendancedata.user_id}
                        sx={tableRowSxTimeline}
                      >
                        {visibleHeaders.map((header) =>
                          renderStableBodyCell(header.key, attendancedata),
                        )}
                        {attendancedata?.attendance?.map((dayDetails, idx) => {
                          // Same three reasons ListView.js's per-row edit
                          // icon gates on (see AttendanceStatus/Status/
                          // ListView.js): no editor permission, editing
                          // your own record (only an admin may), or an
                          // Unassigned ("UA") shift. This used to only
                          // surface as a toast fired on click, so every day
                          // cell looked identically clickable regardless of
                          // whether any of these applied. A cell with no
                          // legend at all has no data to act on either way
                          // (the original code's click handler already did
                          // nothing for that case).
                          const hasLegend = Boolean(dayDetails?.legend);
                          const isUnassignedShift = dayDetails?.legend === "UA";
                          const isOwnRecord =
                            attendancedata?.user_id === currentUserDetails?.id;
                          const isAdmin = [
                            adminRoleName,
                            supportLabel,
                          ].includes(currentUserDetails?.type);
                          const canEditCell =
                            hasLegend &&
                            check_access &&
                            !isUnassignedShift &&
                            (!isOwnRecord || isAdmin);
                          // Same priority the original toasts used: missing
                          // permission first, then self-edit, then
                          // unassigned shift.
                          const deniedMessage = !hasLegend
                            ? null
                            : !check_access
                              ? "Access denied: You are not authorized to modify this data"
                              : isOwnRecord
                                ? "Your data cannot be edited by yourself"
                                : isUnassignedShift
                                  ? "Unassigned shifts cannot be edited"
                                  : null;
                          const legendTitle = hasLegend
                            ? statusLabelMap[dayDetails.legend]
                              ? `${dayDetails.legend} — ${statusLabelMap[dayDetails.legend]}`
                              : dayDetails.legend
                            : "";
                          // Keep showing what the day's status means even
                          // when it can't be edited, rather than replacing
                          // it outright — just append why.
                          const tooltipTitle = deniedMessage
                            ? legendTitle
                              ? `${legendTitle} · ${deniedMessage}`
                              : deniedMessage
                            : legendTitle;

                          return (
                          <Mui.TableCell
                            key={idx}
                            align="center"
                            sx={{
                              padding: "4px 2px",
                              minWidth: 38,
                              width: 38,
                            }}
                            onClick={() => {
                              if (!canEditCell) return;
                              setDepartmentId(attendancedata?.department_id);
                              setShiftDetails({
                                user_id: attendancedata?.user_id,
                                shift_filter_date: dayDetails?.date,
                                date: dayDetails?.date
                                  .split("-")
                                  .reverse()
                                  .join("-"),
                                user_name: attendancedata?.user,
                                shift_name: dayDetails?.shift_name,
                                latest_pay_date:
                                  attendancedata?.latest_pay_date,
                                type: dayDetails?.status,
                                image_url: attendancedata?.image_url,
                                employee_id: attendancedata?.employee_id,
                                department_name: attendancedata?.department,
                                user_type: attendancedata?.user_type,
                              });
                              setOpenSidePeek(true);
                            }}
                          >
                            <Mui.Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              placement="top"
                              title={tooltipTitle}
                            >
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: dayDetails.colour_code,
                                  cursor: canEditCell ? "pointer" : "default",
                                  borderRadius: 3,
                                  flexShrink: 0,
                                }}
                              >
                                <Mui.Typography
                                  sx={{
                                    fontSize:
                                      dayDetails?.legend?.length > 2
                                        ? 9
                                        : dayDetails?.legend?.length === 2
                                          ? 11
                                          : 13,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    color: getContrastingTextColor(
                                      dayDetails?.colour_code
                                        ? dayDetails?.colour_code
                                        : "#181818",
                                    ),
                                    whiteSpace: "nowrap",
                                    letterSpacing:
                                      dayDetails?.legend?.length > 2
                                        ? "-0.5px"
                                        : "normal",
                                  }}
                                >
                                  {dayDetails.legend}
                                </Mui.Typography>
                              </div>
                            </Mui.Tooltip>
                          </Mui.TableCell>
                          );
                        })}
                      </Mui.TableRow>
                    ))
                )}
              </Mui.TableBody>
            </Mui.Table>
          </Mui.TableContainer>
        </Mui.Grid>
      )}
      <Mui.Grid container spacing={2} alignItems="center">
        <Mui.Grid item xs={12} sm={12} md={6}></Mui.Grid>
        <Mui.Grid item xs={12} sm={12} md={6} sx={{ textAlign: "center" }}>
          {attendanceDataDetails?.length > 25 && (
            <Mui.TablePagination
              className="custom-pagination"
              rowsPerPageOptions={[25, 50, 75, 100]}
              component="div"
              count={attendanceDataDetails?.length}
              rowsPerPage={leaverowsPerPage}
              page={leavePage}
              onPageChange={handleChangeLeavePage}
              onRowsPerPageChange={handleChangeLeaveRowsPerPage}
            />
          )}
        </Mui.Grid>
      </Mui.Grid>
      {openSidePeek ? (
        <EditCheckInOut
          setOpenSidePeek={setOpenSidePeek}
          openSidePeek={openSidePeek}
          shiftDetails={shiftDetails}
          setEditState={setEditState}
          departmentId={departmentId}
        />
      ) : null}
    </React.Fragment>
  );
};
